<template>
  <div class="container-fluid py-4 pt-5 mt-5">
    <TableBreadCrumbs title="Card Priorities" />
    <!-- Header with Tabs and Create Button -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <!-- Tabs -->
      <ul class="nav nav-tabs" role="tablist">
        <li class="nav-item" role="presentation">
          <button
            class="nav-link"
            :class="{ active: activeTab === 'default' }"
            @click="setActiveTab('default')"
            type="button"
          >
            Default
          </button>
        </li>
        <li class="nav-item" role="presentation">
          <button
            class="nav-link"
            :class="{ active: activeTab === 'byusers' }"
            @click="setActiveTab('byusers')"
            type="button"
          >
            By Users
          </button>
        </li>
      </ul>

      <!-- Create Button - Only show for Default tab -->
      <button
        v-if="activeTab === 'default'"
        class="btn btn-primary"
        @click="openCreateModal"
        :disabled="!canCreate('data')"
      >
        <i class="fa fa-plus me-2"></i>
        Create New Priority
      </button>
    </div>

    <!-- Tab Content -->
    <div class="card">
      <div class="card-header">
        <h5 class="card-title mb-0">
          {{ currentTabTitle }}
        </h5>
      </div>

      <div class="card-body">
        <!-- Loading State -->
        <div v-if="isLoading" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p class="mt-2">
            Loading {{ activeTab === "default" ? "Default" : "By Users" }} card
            priorities...
          </p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="alert alert-danger">
          <div class="d-flex align-items-center">
            <i class="fa fa-exclamation-triangle me-2"></i>
            <div class="flex-grow-1"><strong>Error:</strong> {{ error }}</div>
            <button
              class="btn btn-sm btn-outline-danger"
              @click="fetchCardTypes"
            >
              <i class="fa fa-refresh"></i> Retry
            </button>
          </div>
        </div>

        <!-- Card Types Table -->
        <div v-else-if="cardTypes.length > 0">
          <CommonTable2
            :tableObj="tableConfig"
            :title="''"
            @edit="handleEdit"
            @delete="handleDelete"
            @view="handleView"
            permissionKey="data"
          />
        </div>

        <!-- Empty State -->
        <div v-else class="text-center py-5">
          <div class="mb-3">
            <i class="fa fa-credit-card fa-3x text-muted"></i>
          </div>
          <h5 class="text-muted">
            No {{ activeTab === "default" ? "Default" : "By Users" }} Card
            Priorities Found
          </h5>
          <p class="text-muted">
            {{
              activeTab === "default"
                ? "No default card priorities are available."
                : "No By Users card priorities are available."
            }}
          </p>
          <div class="mt-3">
            <button
              v-if="activeTab === 'default'"
              class="btn btn-primary me-2"
              @click="openCreateModal"
              :disabled="!canCreate('data')"
            >
              <i class="fa fa-plus me-1"></i>
              Create Card Priority
            </button>
            <button class="btn btn-outline-secondary" @click="fetchCardTypes">
              <i class="fa fa-refresh me-1"></i>
              Refresh
            </button>
          </div>
        </div>
      </div>

        <!-- pagination -->
      <div
        class="d-flex justify-content-end align-items-center gap-3 mt-3 mb-4"
      >
        <!-- Rows per page -->
        <div class="d-flex align-items-center gap-2">
          <span class="text-muted">Rows per page:</span>
          <select
            class="form-select form-select-sm rows-select"
            v-model="tablePagination.per_page"
            @change="changeRows"
          >
            <option v-for="n in [5, 10, 20, 50, 100]" :key="n" :value="n">
              {{ n }}
            </option>
          </select>
        </div>
        <!-- Pagination Buttons -->
        <div class="pagination-mini">
          <!-- Previous -->
          <button
            class="page-btn prev_btn"
            :disabled="!tablePagination.has_prev"
            @click="goToPage(tablePagination.current_page - 1)"
          >
            «
          </button>
          <!-- Numbered Pages -->
          <button
            v-for="(p, index) in visiblePages"
            :key="index"
            class="page-btn"
            :class="{
              'page-active': p === tablePagination.current_page,
              'disabled-dots': p === '...',
            }"
            :disabled="p === '...'"
            @click="p !== '...' && goToPage(p)"
          >
            {{ p }}
          </button>

          <!-- Next -->
          <button
            class="page-btn next_btn"
            :disabled="!tablePagination.has_next"
            @click="goToPage(tablePagination.current_page + 1)"
          >
            »
          </button>
        </div>
      </div>

      <!-- Summary Footer -->
      <div v-if="cardTypes.length > 0" class="card-footer bg-light">
        <div class="d-flex justify-content-between align-items-center">
          <small class="text-muted">
            Showing {{ cardTypes.length }}
            {{ activeTab === "default" ? "default" : "By Users" }} card
            priority{{ cardTypes.length !== 1 ? "s" : "" }}
          </small>
          <button
            class="btn btn-sm btn-outline-secondary"
            @click="fetchCardTypes"
          >
            <i class="fa fa-refresh me-1"></i>
            Refresh
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Create Modal -->
  <BaseModal
    id="createNew"
    :title="`Create ${pageTitle}`"
    :visible="isCreateModalOpen"
    @close="closeCreateModal"
    @save="handleSaveCreate"
    :save-button-disabled="isCreateFormValid"
  >
    <template #body>
      <BaseForm
        :fields="createFormFields"
        v-model="newItemData"
        ref="createFormRef"
      />

      <!-- Color Preview -->
      <div class="mb-3" v-if="newItemData.color">
        <label class="form-label">Color Preview</label>
        <div class="color-preview">
          <div
            class="color-preview-swatch"
            :style="{ backgroundColor: newItemData.color }"
          ></div>
          <small class="text-muted">{{
            newItemData.color.toUpperCase()
          }}</small>
        </div>
      </div>
    </template>
  </BaseModal>

  <!-- Edit/View Modal -->
  <BaseModal
    id="editModal"
    :title="`${isViewMode ? 'View' : 'Edit'} ${pageTitle}`"
    :visible="isEditModalOpen"
    @close="closeEditModal"
    @save="handleSaveEdit"
    :show-save-button="!isViewMode"
    :save-button-disabled="isEditFormValid"
  >
    <template #body>
      <BaseForm
        :fields="editFormFields"
        v-model="editingItemData"
        ref="editFormRef"
      />

      <!-- Color Preview -->
      <div class="mb-3" v-if="editingItemData.color">
        <label class="form-label">Color Preview</label>
        <div class="color-preview">
          <div
            class="color-preview-swatch"
            :style="{ backgroundColor: editingItemData.color }"
          ></div>
          <small class="text-muted">{{
            editingItemData.color.toUpperCase()
          }}</small>
        </div>
      </div>
    </template>
  </BaseModal>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, watch, defineAsyncComponent } from "vue";
import { useCrud } from "@/composables/commen/useCrud";
import BaseModal from "@/components/commen/modal/BaseModal.vue";
import BaseForm from "@/components/commen/modal/BaseForm.vue"; 
import apiService from "@/services/apiService";
import CommonTable from "@/components/commen/table/CommonTable.vue";
import TableBreadCrumbs from "@/components/commen/table/TableBreadCrumbs.vue";
import { usePermissions } from "@/composables/usePermissions";
const { canCreate } = usePermissions();
import CommonTable2 from "@/components/commen/table/CommonTable2.vue";


// Define IconUploader as async component
const IconPicker = defineAsyncComponent({
  loader: () => import("@/components/commen/modal/IconUploader.vue"),
  loadingComponent: {
    template: `
      <div class="text-center py-3">
        <div class="spinner-border spinner-border-sm" role="status"></div>
        <span class="ms-2">Loading icon picker...</span>
      </div>
    `,
  },
  errorComponent: {
    template: `
      <div class="alert alert-warning">
        <small>Icon picker unavailable. Using manual selection.</small>
      </div>
    `,
  },
  delay: 200,
  timeout: 3000,
});

const API_RESOURCE = "/common/cardpriority";
const pageTitle = "Card Priorities";
const title = "Card Priorities List";
const crud = useCrud(API_RESOURCE);

// Tab state
const activeTab = ref<"default" | "byusers">("default");
const isLoading = ref(false);
const error = ref<string | null>(null);
const cardTypes = ref<any[]>([]);
const pagination = ref<any[]>([]) ;

// Computed properties
const currentTabTitle = computed(() =>
  activeTab.value === "default"
    ? "Default Card Priorities"
    : "By Users Card Priorities"
);

// pagination local UI state (mirrors backend, but we keep per_page from UI)
const tablePagination = ref({
  current_page: 1,
  per_page: 10,
  total: 0,
  total_pages: 0,
  has_next: false,
  has_prev: false,
});

const currentPage = ref(1);
// visiblePages computed (same advanced logic)
const visiblePages = computed(() => {
  const total = tablePagination.value.total_pages;
  const current = tablePagination.value.current_page;

  if (!total || total <= 4) {
    return Array.from({ length: Math.max(1, total) }, (_, i) => i + 1);
  }

  const pages: (number | string)[] = [];
  pages.push(1);

  if (current > 4) pages.push("...");

  const windowStart = Math.max(2, current - 1);
  const windowEnd = Math.min(total - 1, current + 1);
  for (let i = windowStart; i <= windowEnd; i++) pages.push(i);

  if (current < total - 3) pages.push("...");

  pages.push(total);
  return pages;
});
// Sync backend pagination into tablePagination, but DO NOT overwrite UI per_page except if UI didn't set it.
watch(
  () => pagination.value,
  (newVal) => {
    if (!newVal) return;

    tablePagination.value.current_page = newVal.current_page || 1;
    tablePagination.value.total_pages = newVal.total_pages || 1;
    tablePagination.value.total = newVal.total || 0;

    // keep UI rows per page unless backend changed
    tablePagination.value.per_page = tablePagination.value.per_page;
    tablePagination.value.has_prev = tablePagination.value.current_page > 1;
    tablePagination.value.has_next =
      tablePagination.value.current_page < tablePagination.value.total_pages;
  },
  { immediate: true, deep: true }
);

// --- Table ---
const tableConfig = computed(() => ({
  columns: [
    { name: "Title", field: "title", sortable: true },
    {
      name: "Color",
      field: "color",
      sortable: false,
      render: (value: string) => {
        if (!value) return '<span class="text-muted">No color</span>';
        return `
          <div class="d-flex align-items-center">
            <div class="color-swatch me-2" style="width: 20px; height: 20px; background-color: ${value}; border: 1px solid #ccc; border-radius: 3px;"></div>
            <span class="text-uppercase">${value}</span>
          </div>
        `;
      },
    },
  ],
  data: cardTypes.value,
  actions: activeTab.value === "default" ? [true] : [false],
  loading: isLoading.value,
}));

// --- Modals ---
const isCreateModalOpen = ref(false);
const isEditModalOpen = ref(false);
const isViewMode = ref(false);

// --- Form Data ---
const newItemData = ref({ title: "", color: "#007bff" });
const editingItemData = ref({ _id: "", title: "", color: "#007bff" });

// --- Form Fields (with color) ---
const createFormFields = [
  {
    key: "title",
    label: "Title",
    type: "text" as const,
    required: true,
    placeholder: "Enter title",
  },
  {
    key: "color",
    label: "Color",
    type: "color" as const,
    required: true,
    placeholder: "Select color",
  },
];
const editFormFields = computed(() => [
  {
    key: "title",
    label: "Title",
    type: "text" as const,
    required: true,
    placeholder: "Enter title",
    readonly: isViewMode.value,
  },
  {
    key: "color",
    label: "Color",
    type: "color" as const,
    required: true,
    placeholder: "Select color",
    readonly: isViewMode.value,
  },
]);

// --- Validation ---
const isCreateFormValid = computed(
  () => !newItemData.value.title.trim() || !newItemData.value.color
);
const isEditFormValid = computed(
  () => !editingItemData.value.title?.trim() || !editingItemData.value.color
);

// Tab methods
const setActiveTab = (tab: "default" | "byusers") => {
  activeTab.value = tab;
  fetchCardTypes();
};

const fetchCardTypes = async () => {
  isLoading.value = true;
  error.value = null;

  try {
    console.log(`Fetching ${activeTab.value} card priorities...`);
    const isDefault = activeTab.value === "default";
    const endpoint =
  `/common/cardpriority-filtered?default=${isDefault}` +
  `&page=${tablePagination.value.current_page}` +
  `&limit=${tablePagination.value.per_page}`;

    const response = await apiService.get(endpoint);
    console.log("Card Types API Response:", response);
    if(response.pagination){
      pagination.value = response.pagination;
    }

    if (Array.isArray(response)) {
      cardTypes.value = response;
      console.log(
        `Loaded ${cardTypes.value.length} ${activeTab.value} card types`
      );
    } else if (response && response.data && Array.isArray(response.data)) {
      cardTypes.value = response.data;
      console.log(
        `Loaded ${cardTypes.value.length} ${activeTab.value} card types`
      );
    } else {
      console.error("Unexpected response structure:", response);
      throw new Error("Invalid response format");
    }
  } catch (err: any) {
    console.error("Error fetching card types:", err);
    error.value =
      err?.response?.data?.message ||
      err?.message ||
      "Failed to load card types";
  } finally {
    isLoading.value = false;
  }
};

const changeRows = async () => {
  tablePagination.value.current_page = 1;  
  await fetchCardTypes();
};

const goToPage = async (page: number) => {
  if (page < 1 || page > tablePagination.value.total_pages) return;

  tablePagination.value.current_page = page;

  await fetchCardTypes();
};



const openCreateModal = () => {
  isCreateModalOpen.value = true;
};

// --- Handlers ---
const handleEdit = (row: any) => {
  isViewMode.value = false;
  editingItemData.value = { ...row };
  isEditModalOpen.value = true;
};
const handleView = (row: any) => {
  isViewMode.value = true;
  editingItemData.value = { ...row };
  isEditModalOpen.value = true;
};
const handleDelete = async (id: string) => {
  if (confirm("Are you sure?")) {
    await crud.deleteItem(id);
    await fetchCardTypes(); // Refresh the current tab data
  }
};

const handleSaveCreate = async () => {
  if (isCreateFormValid.value) return;
  const payload = { ...newItemData.value };
  const result = await crud.createItem(payload);
  if (result) {
    closeCreateModal();
    await fetchCardTypes(); // Refresh the current tab data
  }
};

const handleSaveEdit = async () => {
  const id = editingItemData.value._id;
  if (!id) return;
  const { _id, ...updatePayload } = editingItemData.value;
  const result = await crud.updateItem(id, updatePayload);
  if (result) {
    closeEditModal();
    await fetchCardTypes(); // Refresh the current tab data
  }
};

const closeCreateModal = () => {
  isCreateModalOpen.value = false;
  newItemData.value = { title: "", color: "#007bff" };
};
const closeEditModal = () => {
  isEditModalOpen.value = false;
  isViewMode.value = false;
  editingItemData.value = { _id: "", title: "", color: "#007bff" };
};

onMounted(async () => {
  await fetchCardTypes();
});
</script>

<style scoped>
.color-swatch {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  vertical-align: middle;
}

/* Color input styling */
:deep(.form-control[type="color"]) {
  width: 60px;
  height: 38px;
  padding: 2px;
  border: 1px solid #ced4da;
  border-radius: 0.375rem;
  cursor: pointer;
}

:deep(.form-control[type="color"]:focus) {
  border-color: #86b7fe;
  outline: 0;
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
}

/* Color preview in form */
.color-preview {
  display: flex;
  align-items: center;
  gap: 10px;
}

.color-preview-swatch {
  width: 30px;
  height: 30px;
  border: 2px solid #dee2e6;
  border-radius: 6px;
  display: inline-block;
}
  
.nav-tabs .nav-link {
  border-bottom: 2px solid transparent;
  color: #6c757d;
  font-weight: 500;
}

.nav-tabs .nav-link.active {
  background-color: transparent;
  border-color: transparent transparent #35bfbf transparent;
  color: #35bfbf;
  font-weight: 700;
}

.nav-tabs .nav-link:hover {
  border-color: transparent transparent #dee2e6 transparent;
  color: #495057;
}

/* pagination style */
.rows-select {
  width: 82px;
  padding: 8px 17px;
  font-size: 0.85rem;
  background-color: transparent;
}

/* Mini Pagination */
.pagination-mini {
  display: flex;
  align-items: center;
  border-radius: 6px;
  overflow: hidden;
}

.page-btn {
  border: none;
  background: transparent;
  font-size: 1rem;
  padding: 7px 12px;
  cursor: pointer;
  color: #6c757d;
  border-radius: 0px;
  transition: 0.2s;
  border: 1px solid #dee2e6;
  outline: none;
}
.page-btn:first-child {
  border-top-left-radius: 6px;
  border-bottom-left-radius: 6px;
}
.page-btn:last-child {
  border-top-right-radius: 6px;
  border-bottom-right-radius: 6px;
}
.page-btn:hover {
  background: #e9ecef;
}

.page-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Active page (highlight like the screenshot) */
.page-active {
  background: #21c1d6;
  color: #fff;
  padding: 7px 12px;
  font-weight: 600;
  min-width: 28px;
  text-align: center;
  display: inline-block;
  border: 1px solid #21c1d6;
}
.page-active:hover {
  background: #21c1d6;
}
.prev_btn,
.next_btn {
  color: #21c1d6;
} 
</style>
